import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router';
import { BreadcrumbProps } from '../../stories/Breadcrumb';

interface UidToRefMapping {
  [uid: string]: string;
}

interface LoadingUids {
  [uid: string]: boolean;
}

interface InjectedBreadcrumb {
  id: string;
  index: number;
  breadcrumbs: BreadcrumbProps[];
}

interface BreadcrumbContextType {
  currentRoute: string;
  uidToRefMapping: UidToRefMapping;
  loadingUids: LoadingUids;
  setUidRef: (uid: string, ref: string) => void;
  setUidLoading: (uid: string, loading: boolean) => void;
  displayRoute: string;
  navigationRoute: string; // Add this for navigation with original UIDs
  injectedBreadcrumbs: InjectedBreadcrumb[];
  injectBreadcrumbs: (id: string, index: number, breadcrumbs: BreadcrumbProps[]) => void;
  removeInjectedBreadcrumbs: (id: string) => void;
  concealedSections: string[];
  setConcealedSections: (sections: string[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }
  return context;
};

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [currentRoute, setCurrentRoute] = useState<string>('');
  const [uidToRefMapping, setUidToRefMapping] = useState<UidToRefMapping>({});
  const [loadingUids, setLoadingUids] = useState<LoadingUids>({});
  const [injectedBreadcrumbs, setInjectedBreadcrumbs] = useState<InjectedBreadcrumb[]>([]);
  const [concealedSections, setConcealedSections] = useState<string[]>([]);

  useEffect(() => {
    setCurrentRoute(location.pathname);
  }, [location.pathname]);

  const setUidRef = useCallback((uid: string, ref: string) => {
    setUidToRefMapping((prev) => ({
      ...prev,
      [uid]: ref,
    }));
    // Clear loading state when ref is set
    setLoadingUids((prev) => {
      const newState = { ...prev };
      delete newState[uid];
      return newState;
    });
  }, []);

  const setUidLoading = useCallback((uid: string, loading: boolean) => {
    setLoadingUids((prev) => ({
      ...prev,
      [uid]: loading,
    }));
  }, []);

  const injectBreadcrumbs = useCallback((id: string, index: number, breadcrumbs: BreadcrumbProps[]) => {
    setInjectedBreadcrumbs((prev) => {
      // Remove any existing breadcrumbs with the same id
      const filtered = prev.filter((item) => item.id !== id);
      // Only add new breadcrumbs if they're not empty
      if (breadcrumbs.length > 0) {
        return [...filtered, { id, index, breadcrumbs }];
      }
      // If breadcrumbs are empty, just return the filtered list (don't add empty breadcrumbs)
      return filtered;
    });
  }, []);

  const removeInjectedBreadcrumbs = useCallback((id: string) => {
    setInjectedBreadcrumbs((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const setConcealedSectionsCallback = useCallback((sections: string[]) => {
    setConcealedSections(sections);
  }, []);

  const displayRoute = useMemo(() => {
    let route = currentRoute;

    // Replace UIDs with refs in the route
    Object.entries(uidToRefMapping).forEach(([uid, ref]) => {
      route = route.replace(uid, ref);
    });

    return route;
  }, [currentRoute, uidToRefMapping]);

  // Add navigationRoute that preserves original UIDs
  const navigationRoute = useMemo(() => {
    return currentRoute; // Keep original route with UIDs for navigation
  }, [currentRoute]);

  return (
    <BreadcrumbContext.Provider
      value={{
        currentRoute,
        uidToRefMapping,
        loadingUids,
        setUidRef,
        setUidLoading,
        displayRoute,
        navigationRoute,
        injectedBreadcrumbs,
        injectBreadcrumbs,
        removeInjectedBreadcrumbs,
        concealedSections,
        setConcealedSections: setConcealedSectionsCallback,
      }}
    >
      {children}
    </BreadcrumbContext.Provider>
  );
};
